/**
 * РЕГИСТРАЦИЯ ПУША В НАТИВНОЙ ОБОЛОЧКЕ (Android/Capacitor).
 *
 * Постановка (Антон через Момо, 2026-09-21): APK собирается, плагин
 * `@capacitor/push-notifications` в сборке, google-services.json на месте, но
 * приложение НЕ получает FCM-токен и НЕ регистрирует pusher в Synapse —
 * доставлять пуш физически некуда. Этот модуль закрывает клиентскую половину:
 * разрешение → FCM registration token → `POST /_matrix/client/v3/pushers/set`.
 *
 * ─── ПОЧЕМУ КОД ЛЕЖИТ В ВЕБ-КЛИЕНТЕ, А НЕ В ОБОЛОЧКЕ ────────────────────────
 * Замер по репозиторию оболочки (`alg/messenger-android`, master `7506e191`):
 * `capacitor.config.json` = `{ appId, appName, webDir: "www" }` — БЕЗ
 * `server.url`. По коду `@capacitor/android` 8.5.2
 * (`Bridge.loadWebView()` + `WebViewLocalServer.hostAssets("public")`) это
 * значит: страница берётся из АССЕТОВ APK (`android/app/src/main/assets/public`,
 * куда `npx cap copy` кладёт содержимое `webDir`), а не с devapp по сети.
 * Следовательно единственное место, где может жить JS-логика пуша, — это наш
 * веб-клиент: он же и есть содержимое `www`. (Если однажды появится
 * `server.url` на devapp — код всё равно тот же самый, меняется только способ
 * доставки бандла.)
 *
 * ─── БРАУЗЕР: МОЛЧА НИЧЕГО ───────────────────────────────────────────────────
 * Веб-клиент один на браузер и на оболочку, а обычный Web Push (VAPID,
 * `src/notifications/push.ts` + `push-gateway/`) трогать нельзя. Поэтому:
 *   • нативность определяется по ГЛОБАЛУ `window.Capacitor`, который нативный
 *     мост впрыскивает ДО скриптов страницы (замер: `Bridge.loadWebView()` →
 *     `WebViewCompat.addDocumentStartJavaScript(injector.getScriptString())`,
 *     а `JSExport.getGlobalJS`/`native-bridge.js` ставят `isNativePlatform`,
 *     `isPluginAvailable`, `Plugins`, `PluginHeaders`);
 *   • сам плагин подключается ТОЛЬКО динамическим `import()` внутри нативной
 *     ветки. В браузере модуль `@capacitor/push-notifications` (и `@capacitor/core`
 *     вместе с ним) не загружается вообще — vite выносит его в отдельный чанк.
 *     Импортировать `@capacitor/core` статически было бы вредно: при загрузке
 *     он САМ создаёт `window.Capacitor` (`initCapacitorGlobal`), то есть менял
 *     бы глобальное состояние браузерной страницы без нужды.
 * Итог: в браузере `startNativePush`/`unregisterNativePush` — no-op без сети,
 * без запросов разрешений, без записи в localStorage и без единого лога.
 *
 * ─── АДРЕС ПУШ-ШЛЮЗА НЕ ВЫДУМЫВАЕТСЯ ────────────────────────────────────────
 * `pusher.data.url` обязан указывать на шлюз, УМЕЮЩИЙ FCM. Замер (2026-09-21):
 * наш `push-gateway/server.mjs` — это Web Push/VAPID мост
 * (`webpush.sendNotification(record.subscription, …)`, `pushkey` жёстко
 * `^[a-f0-9]{48}$`), FCM он не умеет и FCM-токен отверг бы как невалидный
 * `pushkey`. Sygnal в наших репозиториях отсутствует. Значит рабочего адреса
 * для Android СЕЙЧАС НЕТ, и придумывать его нельзя.
 * Поэтому адрес, `app_id` и `format` берутся из конфигурации:
 *   • сборка: `VITE_ANDROID_PUSH_GATEWAY_URL`, `VITE_ANDROID_PUSH_APP_ID`,
 *     `VITE_ANDROID_PUSH_FORMAT`;
 *   • ручная проверка на устройстве без пересборки: localStorage-ключи
 *     `alg.push.native.url` / `alg.push.native.appId` / `alg.push.native.format`.
 *     `format` = `"none"` — не отправлять поле `format` вовсе (пустое
 *     значение значит «не задано» → берётся значение по умолчанию).
 * Если адрес не задан — pusher НЕ регистрируется (нечего регистрировать),
 * токен не запрашивается, в консоль уходит запись только под `algDebug=1`.
 */
import type { MatrixClient } from "matrix-js-sdk";
import { debugLog } from "../debug";

/** Значение события `registration` плагина: на Android это FCM-токен. */
interface PushToken {
  value: string;
}
interface PushRegistrationError {
  error: string;
}
interface PushPermissionStatus {
  receive: "prompt" | "prompt-with-rationale" | "granted" | "denied";
}
interface PushListenerHandle {
  remove: () => Promise<void>;
}
/** Только те методы плагина, которыми мы пользуемся (полный тип — в плагине). */
interface PushNotificationsApi {
  checkPermissions: () => Promise<PushPermissionStatus>;
  requestPermissions: () => Promise<PushPermissionStatus>;
  register: () => Promise<void>;
  unregister: () => Promise<void>;
  createChannel: (channel: Record<string, unknown>) => Promise<void>;
  addListener: {
    (event: "registration", fn: (token: PushToken) => void): Promise<PushListenerHandle>;
    (event: "registrationError", fn: (error: PushRegistrationError) => void): Promise<PushListenerHandle>;
  };
}

interface CapacitorBridgeGlobal {
  isNativePlatform?: () => boolean;
  getPlatform?: () => string;
  isPluginAvailable?: (name: string) => boolean;
}

/**
 * Нативная оболочка С РАБОЧИМ плагином пуша — или null.
 *
 * Два условия, оба обязательны:
 *   1. `isNativePlatform()` — мы внутри WebView оболочки, а не в браузере;
 *   2. `isPluginAvailable("PushNotifications")` — плагин реально слинкован в
 *      APK. Замер (`native-bridge.js`): в нативе это проверка наличия
 *      `Capacitor.Plugins.PushNotifications`, то есть отсутствие плагина в
 *      сборке мы увидим ДО первого вызова, а не исключением из моста.
 * Ни один шаг не делает побочных эффектов, поэтому вызов безопасен всегда.
 */
function nativePushBridge(): CapacitorBridgeGlobal | null {
  if (typeof window === "undefined") return null;
  const cap = (window as unknown as { Capacitor?: CapacitorBridgeGlobal }).Capacitor;
  if (!cap) return null;
  try {
    if (cap.isNativePlatform?.() !== true) return null;
    if (cap.isPluginAvailable?.("PushNotifications") !== true) return null;
  } catch {
    return null;
  }
  return cap;
}

/** Значение из localStorage имеет приоритет над сборочным: ручная проверка на устройстве. */
function setting(localKey: string, envValue: string | undefined): string {
  try {
    const local = localStorage.getItem(localKey);
    if (local && local.trim()) return local.trim();
  } catch {
    /* приватный режим / запрет хранилища — берём сборочное значение */
  }
  return (envValue ?? "").trim();
}

export interface NativePushTarget {
  /** Абсолютный https-URL пуш-шлюза (Sygnal). Пустая строка = шлюз не задан. */
  url: string;
  /** `app_id` pusher'а. ОБЯЗАН совпадать с записью нашего приложения в конфиге шлюза. */
  appId: string;
  /**
   * `pusher.data.format`. Пустая строка — поле не отправляется вовсе.
   *
   * ОТЛИЧИЕ «НЕ ЗАДАНО» ОТ «НЕ НУЖНО» (найдено замером, сценарий L):
   * пустое значение в конфиге — это «не задано», и берётся значение по
   * умолчанию. Чтобы СОЗНАТЕЛЬНО убрать поле (шлюз требует полный
   * payload), нужно явное слово `none` — иначе «забыл заполнить» и «решил
   * отключить» были бы одним и тем же вводом.
   */
  format: string;
}

/**
 * `app_id` по умолчанию — reverse-DNS пакета оболочки плюс `.android`
 * (пакет из `android/app/build.gradle`: `games.absolutelive.messenger`).
 * Это ДОГОВОРЁННОСТЬ, а не измеренное значение: конфига шлюза не существует,
 * сверять не с чем. Когда Sygnal появится, значение обязан подтвердить Момо —
 * `app_id` в pusher'е и в `sygnal.yaml` должны быть буква в букву.
 */
const DEFAULT_APP_ID = "games.absolutelive.messenger.android";

/**
 * `event_id_only` выбран осознанно: шлюз получает только room_id/event_id и
 * счётчики, без текста сообщения и без имени отправителя — наружу (Google FCM)
 * содержимое переписки не уходит. Переопределяется конфигом, если шлюз
 * потребует иного.
 */
const DEFAULT_FORMAT = "event_id_only";

/**
 * URL шлюза по умолчанию — тот же подход, что и для `app_id`/`format` выше:
 * забытая переменная сборки не должна молча ломать пуши.
 * Замер (2026-09-27): в `.env.production` переменная `VITE_ANDROID_PUSH_GATEWAY_URL`
 * отсутствовала, из-за чего `url` был пустой строкой и pusher не регистрировался.
 * Путь БЕЗ префикса `/push` — Synapse жёстко валидирует его
 * (`synapse/push/httppusher.py:157`), иначе 400 «Config Error».
 */
const DEFAULT_GATEWAY_URL = "https://devapp.algsuccess.com/_matrix/push/v1/notify";

export function nativePushTarget(): NativePushTarget {
  const configuredFormat = setting("alg.push.native.format", import.meta.env.VITE_ANDROID_PUSH_FORMAT) || DEFAULT_FORMAT;
  return {
    url: setting("alg.push.native.url", import.meta.env.VITE_ANDROID_PUSH_GATEWAY_URL) || DEFAULT_GATEWAY_URL,
    appId: setting("alg.push.native.appId", import.meta.env.VITE_ANDROID_PUSH_APP_ID) || DEFAULT_APP_ID,
    format: configuredFormat.toLowerCase() === "none" ? "" : configuredFormat,
  };
}

/**
 * Что мы уже зарегистрировали С ЭТОГО устройства — основа идемпотентности и
 * единственный способ снять УСТАРЕВШИЙ pusher после смены токена.
 *
 * Ключ включает user_id: на одном устройстве могут входить разные аккаунты, и
 * pusher принадлежит аккаунту, а не устройству.
 */
interface StoredRegistration {
  pushkey: string;
  appId: string;
  url: string;
  format: string;
  deviceId: string;
  updatedAt: string;
}

function stateKey(userId: string): string {
  return `alg.push.native.state.${userId}`;
}

function loadState(userId: string): StoredRegistration | null {
  try {
    const raw = localStorage.getItem(stateKey(userId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredRegistration;
    return typeof parsed?.pushkey === "string" && parsed.pushkey ? parsed : null;
  } catch {
    return null;
  }
}

function saveState(userId: string, state: StoredRegistration): void {
  try {
    localStorage.setItem(stateKey(userId), JSON.stringify(state));
  } catch {
    /* без хранилища идемпотентность держится на getPushers() — см. ensurePusher */
  }
}

function clearState(userId: string): void {
  try {
    localStorage.removeItem(stateKey(userId));
  } catch {
    /* нечего чистить */
  }
}

function sameRegistration(a: StoredRegistration | null, b: StoredRegistration): boolean {
  return (
    !!a &&
    a.pushkey === b.pushkey &&
    a.appId === b.appId &&
    a.url === b.url &&
    a.format === b.format &&
    a.deviceId === b.deviceId
  );
}

/**
 * Плагин грузим один раз за жизнь страницы; в браузере эта функция не вызывается.
 *
 * ОБЯЗАТЕЛЬНАЯ ОБЁРТКА `{ plugin }` — не стиль, а исправление реальной ошибки,
 * пойманной замером (`.probe-push/probe-native.mjs`, сценарий C — на настоящем
 * @capacitor/core и настоящем native-bridge.js).
 *
 * `registerPlugin()` в @capacitor/core отдаёт Proxy, у которого ЛЮБОЕ свойство
 * возвращает функцию-обёртку метода — включая `then` (обработаны только
 * `$$typeof`, `toJSON`, `addListener`, `removeListener`). Любая попытка РАЗРЕШИТЬ
 * промис значением-Proxy заставляет движок JS считать его thenable и вызвать
 * `then()` — а мост на это бросает
 * `CapacitorException: "PushNotifications.then()" is not implemented on android`.
 *
 * Замер показал ДВЕ формы одной и той же ловушки, и обе падают на живом
 * устройстве, а не только в стенде:
 *   1. `import(...).then((m) => m.PushNotifications)` — коллбэк возвращает Proxy;
 *   2. `async function f() { return proxy; }` — то же самое, машинерия async тоже
 *      проверяет `.then` у возвращаемого значения.
 * Поэтому обёртка доезжает до вызывающего: наружу отдаётся `{ plugin }`, а сам
 * Proxy НИКОГДА не становится значением промиса.
 */
let pluginPromise: Promise<{ plugin: PushNotificationsApi }> | null = null;
function loadPlugin(): Promise<{ plugin: PushNotificationsApi }> {
  if (!pluginPromise) {
    pluginPromise = import("@capacitor/push-notifications").then((mod) => ({
      plugin: mod.PushNotifications as unknown as PushNotificationsApi,
    }));
  }
  return pluginPromise;
}

/**
 * Регистрация одного токена. ВСЯ идемпотентность здесь.
 *
 * Важное свойство протокола: Matrix различает pusher'ы парой
 * `(app_id, pushkey)`. Повторный `POST /pushers/set` с той же парой ОБНОВЛЯЕТ
 * запись, а не добавляет вторую, поэтому «плодить дубли при каждом запуске»
 * нельзя даже при желании. Настоящая опасность другая: Firebase МЕНЯЕТ токен
 * (переустановка, очистка данных, ротация) — новый токен даёт НОВЫЙ pusher, а
 * старый остаётся навсегда и продолжает получать доставки в мёртвый адрес.
 * Поэтому после успешной регистрации нового токена мы СНИМАЕМ предыдущий
 * pusher этого устройства — и только его.
 *
 * `append: false` эту задачу не решает: по спецификации он убирает pusher'ы с
 * тем же `(app_id, pushkey)` у ДРУГИХ пользователей, а не прежние токены того
 * же пользователя.
 *
 * Пushery других устройств того же аккаунта (второй телефон, планшет) имеют
 * другой `pushkey` и НЕ ТРОГАЮТСЯ — удалять их значило бы глушить пуш на
 * чужом устройстве.
 */
async function ensurePusher(client: MatrixClient, token: string, target: NativePushTarget): Promise<void> {
  const userId = client.getUserId();
  if (!userId) {
    debugLog("nativePush: no user id, pusher not registered");
    return;
  }
  const deviceId = client.getDeviceId() ?? "";
  const desired: StoredRegistration = {
    pushkey: token,
    appId: target.appId,
    url: target.url,
    format: target.format,
    deviceId,
    updatedAt: new Date().toISOString(),
  };
  const previous = loadState(userId);
  if (sameRegistration(previous, desired)) {
    debugLog("nativePush: pusher already registered for this token, nothing to do");
    return;
  }

  // Хранилище могло быть очищено (новая установка, «Очистить данные»), а
  // pusher на сервере остаться. Спрашиваем сервер, чтобы не делать лишнюю
  // запись и не потерять факт регистрации.
  if (!previous) {
    try {
      const { pushers } = await client.getPushers();
      const existing = pushers?.find(
        (p) => p.app_id === target.appId && p.pushkey === token && p.data?.url === target.url,
      );
      if (existing) {
        saveState(userId, desired);
        debugLog("nativePush: pusher for this token already exists on the server");
        return;
      }
    } catch (error) {
      // Не смогли прочитать список — это не повод не регистрироваться:
      // повторный set с той же парой (app_id, pushkey) безопаден.
      debugLog("nativePush: getPushers failed, continuing with set", error);
    }
  }

  const data: { url: string; format?: string } = { url: target.url };
  if (target.format) data.format = target.format;

  await client.setPusher({
    pushkey: token,
    kind: "http",
    app_id: target.appId,
    app_display_name: "alg(success)",
    device_display_name: "alg(success) (Android)",
    lang: navigator.language || "en",
    // false: та же пара (app_id, pushkey) у другого пользователя означает, что
    // устройство сменило владельца — прежняя привязка обязана исчезнуть.
    append: false,
    data,
  });
  saveState(userId, desired);
  debugLog("nativePush: pusher registered", { appId: target.appId, url: target.url, format: target.format });

  if (previous && previous.pushkey !== token) {
    try {
      await client.removePusher(previous.pushkey, previous.appId);
      debugLog("nativePush: stale pusher removed after token rotation");
    } catch (error) {
      // Устаревший pusher не снялся — доставки в мёртвый токен FCM отбросит
      // сам. Ронять регистрацию НОВОГО токена из-за этого нельзя.
      debugLog("nativePush: stale pusher removal failed", error);
    }
  }
}

/**
 * Запуск слоя пуша. Возвращает функцию остановки (снимает слушатели).
 *
 * В браузере — синхронный no-op. В оболочке: разрешение → `register()` →
 * событие `registration` (оно же повторяется при ротации токена Firebase, см.
 * `MessagingService.onNewToken` → `PushNotificationsPlugin.onNewToken`) →
 * `ensurePusher`.
 */
export function startNativePush(client: MatrixClient): () => void {
  if (!nativePushBridge()) return () => {};

  const target = nativePushTarget();
  if (!target.url) {
    // Осознанный отказ: без адреса шлюза pusher бессмыслен, а запрашивать у
    // человека разрешение на уведомления, которые некуда доставить, — обман.
    debugLog("nativePush: gateway url is not configured, push registration skipped");
    return () => {};
  }

  let stopped = false;
  const handles: PushListenerHandle[] = [];
  // Событий registration может прийти несколько подряд; очередь гарантирует,
  // что два ensurePusher не полезут в setPusher/removePusher одновременно.
  let queue: Promise<void> = Promise.resolve();

  void (async () => {
    try {
      const { plugin } = await loadPlugin();
      if (stopped) return;

      // Слушатели ВЕШАЕМ ДО register(): токен из кэша Firebase приходит
      // практически мгновенно, и подписка «после» теряла бы первое событие.
      handles.push(
        await plugin.addListener("registration", (token: PushToken) => {
          if (stopped || !token?.value) return;
          queue = queue
            .then(() => ensurePusher(client, token.value, nativePushTarget()))
            .catch((error) => debugLog("nativePush: pusher registration failed", error));
        }),
      );
      handles.push(
        await plugin.addListener("registrationError", (error: PushRegistrationError) => {
          // Типичные причины: нет google-services.json в сборке, устройство без
          // Google Play Services, нет сети. Видно под algDebug=1.
          debugLog("nativePush: FCM registration error", error?.error);
        }),
      );
      if (stopped) return;

      const permission = await plugin.requestPermissions();
      if (stopped) return;
      if (permission.receive !== "granted") {
        debugLog("nativePush: notifications permission not granted", permission.receive);
        return;
      }

      // Канал нужен, чтобы уведомление имело звук и попадало в «Важные».
      // Если канал с этим id уже есть, Android вызов игнорирует.
      try {
        await plugin.createChannel({
          id: "alg_messages",
          name: "Messages",
          importance: 5,
          visibility: 1,
          vibration: true,
        });
      } catch (error) {
        debugLog("nativePush: notification channel not created", error);
      }
      if (stopped) return;

      await plugin.register();
    } catch (error) {
      debugLog("nativePush: startup failed", error);
    }
  })();

  return () => {
    stopped = true;
    for (const handle of handles) {
      void handle.remove().catch(() => {});
    }
    handles.length = 0;
  };
}

/**
 * Снятие pusher'а — ВЫЗЫВАТЬ ДО логаута.
 *
 * Порядок принципиален: `removePusher` — запрос с access-токеном, а `logout()`
 * токен стирает. Снятый после логаута pusher остался бы на сервере навсегда,
 * и следующий владелец устройства получал бы чужие уведомления.
 *
 * Дополнительно `plugin.unregister()` удаляет токен Firebase на самом
 * устройстве (замер: `FirebaseMessaging.deleteToken()` + `setAutoInitEnabled(false)`),
 * то есть доставка прекращается, даже если серверная часть не отработала.
 *
 * Никогда не бросает исключение: логаут не должен упираться в пуш.
 */
export async function unregisterNativePush(client: MatrixClient): Promise<void> {
  if (!nativePushBridge()) return;

  const userId = client.getUserId();
  const stored = userId ? loadState(userId) : null;
  const target = nativePushTarget();

  if (stored) {
    try {
      await client.removePusher(stored.pushkey, stored.appId);
      debugLog("nativePush: pusher removed on logout");
    } catch (error) {
      debugLog("nativePush: pusher removal on logout failed", error);
    }
  } else {
    debugLog("nativePush: no known pusher for this device, nothing to remove", target.appId);
  }

  try {
    const { plugin } = await loadPlugin();
    await plugin.unregister();
  } catch (error) {
    debugLog("nativePush: plugin unregister failed", error);
  }

  if (userId) clearState(userId);
}
